import { useCallback, useEffect, useRef, useState } from 'react'

/** Hard timeout after which an unconfirmed override is dropped uncondi-
 *  tionally — defends against unforeseen value-equality mismatches that
 *  would otherwise leave the UI stuck on the optimistic value.
 *
 *  10 s budget rationale: the sync push marks each task `synced=true` in
 *  Dexie ONE BY ONE after a Supabase round-trip (~200–500 ms/task). With
 *  10 rapid toggles the last write can arrive at t ≈ 3–5 s. That Dexie
 *  write triggers the LiveQuery → layer-1 cleanup fires naturally. A 3 s
 *  budget was too short and raced the sync writes, causing brief visual
 *  reverts. 10 s gives enough headroom for all sync writes to land. */
const OVERRIDE_HARD_TIMEOUT_MS = 10_000

interface IdentifiedEntity {
  id: string
  updatedAt?: Date | null
}

interface OverrideRecord<T> {
  partial: Partial<T>
  /** When the override was last set/merged. Used by the
   *  hard-timeout cleanup to drop stuck overrides. */
  setAt: number
}

/** Per-key value equality for the field types that mutation handlers write
 *  (Dates compared by epoch, primitive arrays element-wise, plain objects
 *  by 1-level deep equality, everything else by `===` with null/undefined
 *  equivalence). */
function valueMatches(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (!valueMatches(a[i], b[i])) return false
    return true
  }
  if (typeof a === 'object' && typeof b === 'object') {
    // Plain-object deep equality, one level. Cheap fallback for fields
    // whose value happens to be a plain object (rare today, common in
    // future feature shapes). Skips prototypes / class instances.
    const aKeys = Object.keys(a as object)
    const bKeys = Object.keys(b as object)
    if (aKeys.length !== bKeys.length) return false
    for (const k of aKeys) {
      if (!valueMatches((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false
    }
    return true
  }
  return false
}

/**
 * Per-entity partial overrides applied on top of a raw Dexie entity list.
 * Used by pages to render mutation results in the next paint instead of
 * waiting on the IDB write → LiveQuery → re-render roundtrip (~1–2s when
 * sync push/pull cycles are competing for the main thread).
 *
 * Cleanup happens in two layers, top to bottom:
 *  1. **Per-key value match against the raw entity.** As soon as a key's
 *     value in `rawEntities` matches the override, the key is dropped.
 *  2. **Hard timeout (3 s).** Defensive net for any future field type
 *     whose value equality slips through `valueMatches`.
 *
 * Comparing against the **raw** data (not against override-applied data)
 * is critical: comparing against override-applied data would clear keys
 * on the very first render and snap the UI back to its old value until
 * the LiveQuery catches up.
 *
 * NOTE: A previous Layer 2 (`updatedAt > setAt`) was removed because it
 * caused visual reverts during rapid sequential toggles. A concurrent
 * write to a different task can re-fire the LiveQuery and return this
 * task with an advanced `updatedAt` before its own IDB write has settled,
 * causing the override to be dropped prematurely. Per-key field matching
 * is the correct and sufficient mechanism.
 */
export function useOptimisticEntityOverrides<T extends IdentifiedEntity>(
  rawEntities: T[] | undefined,
): {
  optimisticOverrides: Map<string, Partial<T>>
  setOverride: (id: string, partial: Partial<T>) => void
  apply: (entity: T) => T
} {
  const [overrides, setOverrides] = useState<Map<string, OverrideRecord<T>>>(() => new Map())

  const setOverride = useCallback((id: string, partial: Partial<T>) => {
    setOverrides(prev => {
      const next = new Map(prev)
      // Merge with any existing override so rapid successive changes
      // (e.g. toggle complete → set duration) don't overwrite each other
      // before the first one has been confirmed by Dexie.
      const existing = prev.get(id)
      next.set(id, {
        partial: existing ? { ...existing.partial, ...partial } : partial,
        setAt: Date.now(),
      })
      return next
    })
  }, [])

  // Drop override keys whose raw Dexie entity now reflects the same value
  // (layer-1), or which have been around longer than the hard timeout (layer-3).
  useEffect(() => {
    if (!rawEntities || overrides.size === 0) return
    const rawById = new Map<string, T>()
    for (const e of rawEntities) rawById.set(e.id, e)
    const now = Date.now()
    let changed = false
    const next = new Map(overrides)
    for (const [id, record] of overrides) {
      const age = now - record.setAt
      // (3) Hard timeout — unconditional drop.
      if (age > OVERRIDE_HARD_TIMEOUT_MS) {
        next.delete(id)
        changed = true
        continue
      }
      const entity = rawById.get(id)
      if (!entity) continue
      // (1) Per-key cleanup so multi-field overrides settle incrementally.
      const remaining: Partial<T> = {}
      let kept = false
      for (const k of Object.keys(record.partial) as Array<keyof T>) {
        const ev = (entity as any)[k]
        const ov = (record.partial as any)[k]
        if (valueMatches(ev, ov)) continue
        ;(remaining as any)[k] = ov
        kept = true
      }
      if (!kept) {
        next.delete(id)
        changed = true
      } else if (Object.keys(remaining).length !== Object.keys(record.partial).length) {
        next.set(id, { partial: remaining, setAt: record.setAt })
        changed = true
      }
    }
    if (changed) setOverrides(next)
  }, [rawEntities, overrides])

  // Schedule a re-render at the latest `setAt + HARD_TIMEOUT` so the
  // timeout-based cleanup fires even if no other Dexie write triggers
  // a re-render in the meantime.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (overrides.size === 0) return
    let earliestDeadline = Infinity
    for (const r of overrides.values()) {
      const deadline = r.setAt + OVERRIDE_HARD_TIMEOUT_MS
      if (deadline < earliestDeadline) earliestDeadline = deadline
    }
    const wait = Math.max(0, earliestDeadline - Date.now())
    timerRef.current = setTimeout(() => {
      // Bump state to force the cleanup effect to re-run.
      setOverrides(prev => new Map(prev))
    }, wait + 16)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [overrides])

  // Stable, plain Map<id, Partial<T>> view for consumers that want to
  // peek at the override (and don't care about `setAt`).
  const optimisticOverrides = useDerivedPartialMap(overrides)

  const apply = useCallback((entity: T): T => {
    const record = overrides.get(entity.id)
    return record ? { ...entity, ...record.partial } : entity
  }, [overrides])

  return { optimisticOverrides, setOverride, apply }
}

/** Derive a `Map<id, Partial<T>>` view from the internal record map.
 *  Stable per render — only changes when the underlying override set
 *  changes, so downstream `useMemo([overrides])` chains bail correctly. */
function useDerivedPartialMap<T>(records: Map<string, OverrideRecord<T>>): Map<string, Partial<T>> {
  const lastInputRef = useRef<Map<string, OverrideRecord<T>> | null>(null)
  const lastOutputRef = useRef<Map<string, Partial<T>>>(new Map())
  if (records !== lastInputRef.current) {
    const out = new Map<string, Partial<T>>()
    for (const [id, r] of records) out.set(id, r.partial)
    lastOutputRef.current = out
    lastInputRef.current = records
  }
  return lastOutputRef.current
}
